import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Users, Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseDetail = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      toast.error("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#4A4A4A] mb-4">Course not found</p>
          <Link to="/courses" className="brutalist-btn-primary">Back to Courses</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/dashboard" className="flex items-center gap-2 text-[#4A4A4A] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="brutalist-card p-8">
          <div className="flex items-start gap-6 mb-6">
            <span className="text-6xl">{course.language_flag}</span>
            <div>
              <h1 className="text-3xl font-bold mb-2">{course.name}</h1>
              <p className="text-lg text-[#4A4A4A]">{course.language_name}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-[#F0EFEB] rounded-lg p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2" />
              <div className="font-bold">{course.duration_weeks} Weeks</div>
              <div className="text-sm text-[#4A4A4A]">{course.hours_per_class}h / class</div>
            </div>
            <div className="bg-[#F0EFEB] rounded-lg p-4 text-center">
              <Calendar className="w-6 h-6 mx-auto mb-2" />
              <div className="font-bold">1 Class/Week</div>
              <div className="text-sm text-[#4A4A4A]">Live sessions</div>
            </div>
            <div className="bg-[#F0EFEB] rounded-lg p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2" />
              <div className="font-bold capitalize">{course.level}</div>
              <div className="text-sm text-[#4A4A4A]">Level</div>
            </div>
          </div>

          {course.description && (
            <div className="mb-8">
              <h2 className="font-bold text-lg mb-2">About this Course</h2>
              <p className="text-[#4A4A4A]">{course.description}</p>
            </div>
          )}

          {course.tutor_name && (
            <div className="border-2 border-[#0A0A0A] rounded-lg p-6 bg-[#FBE7C6]">
              <h2 className="font-bold text-lg mb-4">Your Tutor</h2>
              <div className="flex items-start gap-4">
                {course.tutor_photo ? (
                  <img src={course.tutor_photo} alt={course.tutor_name} className="w-20 h-20 rounded-full border-2 border-[#0A0A0A] object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-white border-2 border-[#0A0A0A] flex items-center justify-center text-2xl font-bold">
                    {course.tutor_name?.charAt(0)}
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-xl">{course.tutor_name}</h3>
                  {course.tutor_bio && <p className="text-sm text-[#4A4A4A] mt-2">{course.tutor_bio}</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
